/**
 * Rutificador — utilidades de dominio (sin dependencias de React ni de red).
 *
 * Portado del mockup Next.js `src/lib/mock/rutificador.ts`, pero adaptado al
 * dominio real: el dueño de la ruta es el CHOFER (no el vehículo), así que se
 * eliminó todo lo de capacidad/flota (CAPACIDAD_TIPO, fitDe, sugerirTipo,
 * volumenM3) que en el real no tiene datos (GET fleet responde 502).
 *
 * La secuenciación es una aproximación front-side (vecino-más-cercano desde el
 * origen); el VRP real lo resuelve el backend cuando exista el cable.
 */

/** Colores de grupo — distinguibles entre sí y sobre el mapa (light + dark). */
export const GROUP_COLORS = [
  "#F97316", // brand orange
  "#0080FF", // accent blue
  "#9333EA", // violet
  "#0891B2", // cyan
  "#DB2777", // pink
  "#16A34A", // green
  "#CA8A04", // amber
  "#4F46E5", // indigo
];

/**
 * Distancia haversine en kilómetros entre dos coordenadas {lat,lng}.
 */
export function haversineKm(a, b) {
  if (!a || !b) return 0;
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const la1 = (a.lat * Math.PI) / 180;
  const la2 = (b.lat * Math.PI) / 180;
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(la1) * Math.cos(la2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

/**
 * Secuencia la entrega con vecino-más-cercano desde el origen y calcula la ETA
 * de llegada por parada. Aproximación de front — el backend real resuelve el
 * VRP completo.
 *
 * @param {Array<Object>} puntos - Punto[] (ver useRutificadorData). Los que no
 *                                 tengan `geo` se descartan (no se pueden rutear).
 * @param {{lat:number,lng:number}} origen - punto de salida/regreso (base).
 * @param {Object} [opts]
 * @param {number} [opts.velKmh=22]    - velocidad urbana promedio (km/h).
 * @param {number} [opts.salidaMin=540] - minutos-desde-medianoche de salida (9:00).
 * @param {number} [opts.servicioMin=4] - minutos de servicio en cada parada.
 * @returns {{
 *   paradas: Array<{ punto: Object, orden: number, distanciaDesdeAnteriorKm: number, etaLlegadaMin: number }>,
 *   distanciaKm: number, duracionMin: number, salidaMin: number,
 *   regresoMin: number, servicioPorParadaMin: number, origen: (Object|null)
 * }}
 */
export function optimizarGrupo(puntos, origen, opts = {}) {
  const velKmh = opts.velKmh ?? 22;
  const salidaMin = opts.salidaMin ?? 540;
  const servicio = opts.servicioMin ?? 4;

  // Solo se pueden rutear los puntos con coordenada válida.
  const restantes = (puntos || []).filter((p) => p && p.geo);
  const paradas = [];
  let actual = origen || null;
  let reloj = salidaMin;
  let distTotal = 0;
  let orden = 1;

  while (restantes.length) {
    let bestIdx = 0;
    let bestDist = Infinity;
    for (let i = 0; i < restantes.length; i++) {
      // Sin origen aún (base no fijada), la primera parada arranca la cadena.
      const d = actual ? haversineKm(actual, restantes[i].geo) : 0;
      if (d < bestDist) {
        bestDist = d;
        bestIdx = i;
      }
    }
    const punto = restantes.splice(bestIdx, 1)[0];
    const viajeMin = velKmh > 0 ? (bestDist / velKmh) * 60 : 0;
    const llegada = reloj + viajeMin;
    distTotal += bestDist;
    paradas.push({
      punto,
      orden: orden++,
      distanciaDesdeAnteriorKm: Math.round(bestDist * 10) / 10,
      etaLlegadaMin: Math.round(llegada),
    });
    reloj = llegada + servicio;
    actual = punto.geo;
  }

  // Regreso a la base (si hay origen).
  const vueltaKm = paradas.length && origen ? haversineKm(actual, origen) : 0;
  distTotal += vueltaKm;
  const regresoMin = Math.round(
    reloj + (velKmh > 0 ? (vueltaKm / velKmh) * 60 : 0)
  );

  return {
    paradas,
    distanciaKm: Math.round(distTotal * 10) / 10,
    duracionMin: Math.round(regresoMin - salidaMin),
    salidaMin,
    regresoMin,
    servicioPorParadaMin: servicio,
    origen: origen || null,
  };
}

/* ------------------------------ formateadores ----------------------------- */

/** minutos-desde-medianoche → "HH:MM" (24h, envuelve a las 24h). */
export function fmtHora(min) {
  if (min == null || Number.isNaN(min)) return "—";
  const h = Math.floor(min / 60) % 24;
  const m = Math.round(min % 60);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/** minutos → "N h M min" / "M min". `t` viene del componente (i18n). */
export function fmtDuracion(min, t) {
  if (min == null || Number.isNaN(min)) return "—";
  const total = Math.max(0, Math.round(min));
  const h = Math.floor(total / 60);
  const m = total % 60;
  return h > 0 ? t("{{h}} h {{m}} min", { h, m }) : t("{{m}} min", { m });
}

/** kilos → "N kg" / "N,N t". null/sin dato → "—". */
export function fmtPeso(kg) {
  if (kg == null || Number.isNaN(kg)) return "—";
  return kg >= 1000 ? `${(kg / 1000).toFixed(1)} t` : `${Math.round(kg)} kg`;
}

/** cantidad de bultos → "N bulto(s)". `t` viene del componente (i18n). */
export function fmtBultos(n, t) {
  const v = Number(n) || 0;
  return v === 1 ? t("{{n}} bulto", { n: v }) : t("{{n}} bultos", { n: v });
}

/**
 * ¿la coordenada {lat,lng} cae dentro del polígono [{lat,lng}]? (ray-casting).
 * Equivalente a `pointInPolygon` de src/utils/geoUtils.js pero con la firma que
 * usa el rutificador (objetos {lat,lng} en vez de tuplas [lng,lat]).
 */
export function geoEnPoligono(geo, poligono) {
  if (!geo || !Array.isArray(poligono) || poligono.length < 3) return false;
  let inside = false;
  for (let i = 0, j = poligono.length - 1; i < poligono.length; j = i++) {
    const xi = poligono[i].lng;
    const yi = poligono[i].lat;
    const xj = poligono[j].lng;
    const yj = poligono[j].lat;
    const intersect =
      (yi > geo.lat) !== (yj > geo.lat) &&
      geo.lng < ((xj - xi) * (geo.lat - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

/**
 * Docs crudos (de `packages`) de los pedidos de una cesta, tomados del pool ya
 * cargado, para el picking & packing. El detalle de la cesta no sirve para eso:
 * trae un resumen angosto del pedido, sin productos.
 *
 * `faltantes` son los ids de la cesta que el pool no tiene (otro filtro de
 * estado, ya con chofer, fuera del tope de 600...). Imprimir sin ellos daría un
 * picking incompleto que parece completo: la vista bloquea la impresión
 * mientras haya alguno. Sin un Map de docs, todos quedan como faltantes.
 *
 * @param {string[]} packageIds - ids de los pedidos de la cesta.
 * @param {Map<string, Object>} docsPorId - id → doc crudo del pool.
 * @returns {{ docs: Object[], faltantes: string[] }} en el orden de
 *   `packageIds` y sin repetidos.
 */
export function docsDeCesta(packageIds, docsPorId) {
  const docs = [];
  const faltantes = [];
  const vistos = new Set();
  (Array.isArray(packageIds) ? packageIds : []).forEach((id) => {
    if (vistos.has(id)) return;
    vistos.add(id);
    const doc = docsPorId instanceof Map ? docsPorId.get(id) : undefined;
    if (doc) docs.push(doc);
    else faltantes.push(id);
  });
  return { docs, faltantes };
}
